/**
 * Firms API client. Used by the Add Account wizard's firm picker.
 */

import { api } from "../api";
import type {
  FirmChallengeRow,
  FirmChallengesRes,
  SynthesizeChallengesRes,
  SynthesisJob,
} from "@pfh/shared";

/** One row per (firm_id, platform_type) from the firm_platforms table.
 *  Surfaced so the Add Account wizard can prefill the Terminal URL
 *  input with a known-good default instead of forcing the user to
 *  retype a URL that another customer (or we) have already seeded. */
export interface FirmPlatformRow {
  platform_type: string;
  /** TradeLocker Keycloak auth endpoint — distinct from terminal_url.
   *  Null for MT5 / Match-Trader where terminal_url IS the login page. */
  login_url: string | null;
  terminal_url: string | null;
  terminal_url_demo: string | null;
  terminal_url_funded: string | null;
  /** MT5 broker-server string (e.g. "FTMO-Server", "Funded-Live"). Only
   *  populated for platform_type='mt5' rows; null elsewhere. Shown to
   *  the operator in the Credentials step's terminal verification card
   *  so they can confirm it matches their credentials email before the
   *  bot tries to log in. */
  mt5_server: string | null;
}

export interface FirmRow {
  id: string;
  name: string;
  platform_type: string;
  adapter: string;
  market_type?: "forex_cfd" | "futures" | string | null;
  rules_version: string;
  rulebook_hash: string;
  compiled_summary: string | null;
  created_at: number;
  updated_at: number;
  official_url?: string | null;
  pfm_slug?: string | null;
  terminal_url?: string | null;
  terminal_url_demo?: string | null;
  terminal_url_funded?: string | null;
  /** Platforms the firm actually supports (from firm_platforms). The
   *  wizard uses this to filter firms by user-chosen platform. */
  supported_platforms?: string[];
  /** Per-platform URL detail. Same rows as supported_platforms but
   *  with the full URL set so the wizard can resolve the right
   *  default for a given (platform, phase). Empty array when the
   *  firm has no firm_platforms rows yet. */
  platforms?: FirmPlatformRow[];
  // ── Crawl/operator metadata (LEFT JOIN firm_profiles + subqueries).
  //    All optional — firms without a firm_profiles row come back null. ──
  rating?: number | null;
  review_count?: number | null;
  country?: string | null;
  max_allocation?: string | null;
  discount_pct?: number | null;
  discount_code?: string | null;
  offer_description?: string | null;
  /** Epoch ms of the newest firm_intel.scraped_at for this firm; null when never crawled. */
  last_crawled_at?: number | null;
  /** Count of accounts.status='active' for this firm. */
  active_account_count?: number | null;
  /** 0/1 from the latest firm_availability_history row; null when no history. */
  turkey_allowed?: number | null;
  /** Distinct program_name count from firm_challenges (0 when no programs seeded). */
  program_count?: number | null;
}

export async function listFirms(): Promise<FirmRow[]> {
  const res = await api.get<{ firms: FirmRow[] }>("/api/firms");
  return res.firms;
}

/** Re-export the canonical shared shape so wizard code + pages can
 *  import the row type alongside the client. `FirmChallenge` remains
 *  the in-dashboard alias to keep callers that already imported it
 *  working after the shared schema swap. */
export type FirmChallenge = FirmChallengeRow;
export type { FirmChallengeRow, FirmChallengesRes };

/**
 * Fetch a firm's variant catalog from firm_challenges. Feeds the
 * wizard's Program step. Empty `challenges` array just means we
 * haven't seeded this firm yet — the caller can trigger Haiku
 * synthesis via `synthesizeFirmChallenges(slug)` below.
 */
export async function getFirmChallenges(slug: string): Promise<FirmChallengesRes> {
  return api.get<FirmChallengesRes>(
    `/api/firms/${encodeURIComponent(slug)}/challenges`,
  );
}

/**
 * Start a Haiku synthesis run for one firm. Returns immediately with a
 * jobId — the actual work runs on the worker's waitUntil tail. Use
 * `getSynthesisJob` to poll for completion.
 */
export async function synthesizeFirmChallenges(
  slug: string,
): Promise<SynthesizeChallengesRes> {
  return api.post<SynthesizeChallengesRes>(
    `/api/firms/${encodeURIComponent(slug)}/synthesize-challenges`,
    {},
  );
}

/** Fetch current state of a synthesis job — called by the wizard's
 *  spinner on a 2s interval. */
export async function getSynthesisJob(
  slug: string,
  jobId: string,
): Promise<SynthesisJob> {
  return api.get<SynthesisJob>(
    `/api/firms/${encodeURIComponent(slug)}/synthesize-challenges/${encodeURIComponent(jobId)}`,
  );
}

/**
 * Wait for a synthesis job to reach a terminal state. Polls every 2s up
 * to `timeoutMs` (default 120s). Resolves with the final job row;
 * rejects on timeout or `failed` status. Used by the wizard's
 * "Synthesize Now" CTA so the caller just awaits one promise.
 */
export async function waitForSynthesis(
  slug: string,
  jobId: string,
  opts: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<SynthesisJob> {
  const timeoutMs = opts.timeoutMs ?? 120_000;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    if (opts.signal?.aborted) throw new Error("aborted");
    const job = await getSynthesisJob(slug, jobId);
    if (job.status === "completed") return job;
    if (job.status === "failed") {
      throw new Error(job.error ?? "synthesis failed");
    }
    if (Date.now() > deadline) {
      throw new Error(`synthesis timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
}
